import React, { useState } from 'react';

import Icon from 'views/site/Icon.js';

import Section, { SectionHeader } from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * FAQ
 *
 * Objection handling. These are the questions a homeowner actually asks in the
 * living room, answered the way the advisor answers them — including the ones
 * with an awkward answer, because a FAQ that dodges is worth nothing.
 *
 * Answers are grounded in the manuals: the 18-device network limit, the
 * five-minute silence, the sealed non-replaceable battery, the requirement to
 * test weekly, and the fact that the sensors run on their own radio rather
 * than the household Wi-Fi.
 */

const FAQ = ({ dealership, items: provided }) => {

    const mobile = Utils.isMobile();
    const [open, setOpen] = useState(null);

    // The site's own questions, unless a page supplies its own set.
    const items = provided || [{
        answer: 'No. There is no subscription and no monitoring contract, and nothing has to be paid each month to keep the alerts working. The sensors talk to each other on their own private radio network, and the Comm Link sends alerts to your phone at no ongoing cost. If you pay for the network with a payment plan you will have a monthly payment until it is paid off, but that is the network itself rather than a fee for the service, and it ends when the balance does.',
        question: 'Is there a monthly fee?'
    },{
        answer: 'Yes. Payment plans are available, and your advisor can go through the options with you when they have seen the house and know what covering it actually takes. Paying over time does not change what you get or what it costs to run: the network is yours either way, and there is still no monitoring fee.',
        question: 'Can I pay for it over time?'
    },{
        answer: 'The network is self-monitored, which means it watches your home and reports to you. Every device covers its own room and tells every other device the moment it detects something, so the whole house sounds at once. The Comm Link sends a text, an email and a push notification over your internet connection to you and to as many as eight people you choose, wherever you happen to be. You decide who is on that list, and you and your contacts decide what happens next, including calling 911. There is no call center in the middle charging a monthly fee to do that watching for you.',
        question: 'How is the network monitored?'
    },{
        answer: 'The sensors do not use Wi-Fi at all — they run on a proprietary radio at 905.2 and 913.2 MHz, tested to 200 feet in open air, and each one repeats the signal so coverage grows as you add devices. Only the Comm Link touches your internet, and it can use a wired Ethernet connection instead of Wi-Fi. If the internet drops, the alarms still sound throughout the house; you just do not get the phone notification.',
        question: 'What happens if my Wi-Fi goes down?'
    },{
        answer: 'The batteries are sealed lithium manganese and are not replaceable by design — they meet the 10-year tamper-resistant requirement. That is deliberate: the most common reason a smoke alarm fails is a battery that was removed and never put back. When a sensor reaches end of life it tells you, and you replace the unit.',
        question: 'Do I have to change batteries?'
    },{
        answer: 'A network supports up to 18 devices, of which up to 12 can be smoke sensors, and water sensors are not included in this count. That covers all but the largest homes. If yours needs more, your advisor will tell you during the walkthrough rather than after.',
        question: 'How many devices can one home have?'
    },{
        answer: 'Yes — hold the button and the horn silences for five minutes. It also silences any sensor on the network that was triggered remotely. It will not silence a sensor that is detecting the problem directly; you have to press that one. And it does not resolve the situation, so check the house first.',
        question: 'Can I silence it if I burn the toast?'
    },{
        answer: 'Weekly, by pressing the button on any sensor. It runs a full network test — every other device on the network will respond in turn, so you are testing the network rather than one alarm. It takes about ten seconds.',
        question: 'How often should I test it?'
    },{
        answer: `${dealership?.name || 'Your local dealer'} installs it and shows your family how it works, where the escape routes are, and what to do when it sounds. The education is the part most people remember afterwards.`,
        question: 'Do I install this myself?'
    },{
        answer: 'Pricing depends on the size and layout of your home and how many rooms need covering, so it is quoted after the walkthrough rather than guessed at online. Building a quote here costs nothing and commits you to nothing.',
        question: 'Why is there no price on this website?'
    }];

    return (
        <Section id={'faq'} narrow={true}>
            <SectionHeader
            eyebrow={'Questions'}
            subtitle={'The ones that come up in the living room, answered the way we answer them there. Nothing here is a sales answer dressed up as an explanation. If your question is not below it is still worth asking, because most of these ended up on this page because somebody asked.'}
            title={'The things people ask us'} />

            <div style={{
                backgroundColor: Theme.colors.white,
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 16,
                overflow: 'hidden'
            }}>
                {items.map((item, index) => {

                    let expanded = open === index;

                    return (
                        <div
                        key={index}
                        style={{ borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}` }}>
                            <div
                            onClick={() => setOpen(expanded ? null : index)}
                            style={{
                                alignItems: 'center',
                                cursor: 'pointer',
                                display: 'flex',
                                gap: 16,
                                justifyContent: 'space-between',
                                padding: mobile ? '18px 18px' : '20px 24px'
                            }}>
                                <span style={Theme.textStyles.subHeading({ fontSize: mobile ? 16 : 17 })}>
                                    {item.question}
                                </span>
                                <Icon name={'chevron-down'} size={12} color={Theme.colors.primary()} style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }} />
                            </div>
                            {expanded && (
                                <div style={{ padding: mobile ? '0 18px 20px' : '0 24px 24px' }}>
                                    <span style={{
                                        ...Theme.textStyles.body({ fontSize: 15 }),
                                        display: 'block',
                                        maxWidth: '68ch'
                                    }}>{item.answer}</span>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </Section>
    )
}

export default FAQ;
